import { memo, Fragment, ReactNode, useCallback, KeyboardEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import type { DataGridColumn, DataGridSelection } from './DataGrid';
import { Checkbox } from './Checkbox';

export interface DataGridBodyProps<T> {
  columns: DataGridColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  animateRows?: boolean;
  density?: 'comfortable' | 'compact';
  zebra?: boolean;
  emptyState?: ReactNode;
  onRowClick?: (row: T, e?: React.MouseEvent) => void;
  onRowContextMenu?: (row: T, e: React.MouseEvent) => void;
  isRowClickable?: (row: T) => boolean;
  rowClassName?: string | ((row: T) => string);
  renderRowLeading?: (row: T) => ReactNode;
  renderRowActions?: (row: T) => ReactNode;
  renderExpanded?: (row: T) => ReactNode;
  isRowExpandable?: (row: T) => boolean;
  expandedKeys?: Set<string>;
  onToggleExpanded?: (key: string) => void;
  columnWidths?: Record<string, number>;
  selection?: DataGridSelection<T>;
}

function SkeletonRow({
  columns,
  hasActions,
  hasLeading,
  hasExpander,
}: {
  columns: number;
  hasActions: boolean;
  hasLeading: boolean;
  hasExpander: boolean;
}): ReactNode {
  const total = columns + (hasActions ? 1 : 0) + (hasLeading ? 1 : 0) + (hasExpander ? 1 : 0);
  return (
    <tr role="row" className="animate-pulse">
      {Array.from({ length: total }).map((_, i) => (
        <td key={i} role="cell" className="px-4 py-3.5">
          <div className="h-4 bg-white/[0.06] rounded w-3/4" />
        </td>
      ))}
    </tr>
  );
}

function DataGridBodyInner<T>({
  columns,
  data,
  rowKey,
  loading = false,
  animateRows = true,
  density = 'comfortable',
  zebra = false,
  emptyState,
  onRowClick,
  onRowContextMenu,
  isRowClickable,
  rowClassName,
  renderRowLeading,
  renderRowActions,
  renderExpanded,
  isRowExpandable,
  expandedKeys,
  onToggleExpanded,
  columnWidths,
  selection,
}: DataGridBodyProps<T>): ReactNode {
  const cellPad = density === 'compact' ? 'px-4 py-2' : 'px-4 py-3.5';
  const hasLeading = !!renderRowLeading;
  const hasExpander = !!renderExpanded;
  const hasSelection = selection !== undefined;
  const colCount = columns.length + (renderRowActions ? 1 : 0) + (hasLeading ? 1 : 0) + (hasExpander ? 1 : 0) + (hasSelection ? 1 : 0);

  const renderRow = useCallback(
    (item: T, index: number): ReactNode => {
      const key = rowKey(item);
      const clickable = !!onRowClick && (!isRowClickable || isRowClickable(item));
      const expandable = !!renderExpanded && (isRowExpandable?.(item) ?? true);
      const expanded = expandable && !!expandedKeys?.has(key);
      const baseRow = '';
      const hoverCls = clickable
        ? 'hover:bg-white/[0.04] cursor-pointer'
        : 'hover:bg-white/[0.02]';
      const zebraCls =
        zebra && index % 2 === 1 ? 'bg-[var(--color-surface-darker)]/40' : '';
      const customCls =
        typeof rowClassName === 'function' ? rowClassName(item) : (rowClassName ?? '');

      const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>): void => {
        if (!clickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRowClick?.(item);
        }
      };

      return (
        <Fragment key={key}>
          <motion.tr
            layout={animateRows ? 'position' : undefined}
            initial={animateRows ? { opacity: 0, y: -4 } : false}
            animate={animateRows ? { opacity: 1, y: 0 } : undefined}
            exit={animateRows ? { opacity: 0, y: -4 } : undefined}
            transition={animateRows ? { duration: 0.16, ease: 'easeOut' } : undefined}
            role="row"
            tabIndex={clickable ? 0 : -1}
            aria-disabled={!clickable}
            onClick={clickable ? (e) => onRowClick?.(item, e) : undefined}
            onContextMenu={onRowContextMenu ? (e) => onRowContextMenu(item, e) : undefined}
            onKeyDown={handleKeyDown}
            className={`${baseRow} ${hoverCls} ${zebraCls} ${customCls} focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/60`}
          >
            {hasSelection && (
              <td
                role="cell"
                className={`${cellPad} w-12 min-w-12 text-center`}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <Checkbox
                  checked={selection.isRowSelected?.(item) ?? selection.selectedKeys.has(key)}
                  onChange={() => selection.onToggle(item)}
                  disabled={selection.disabled}
                  ariaLabel={`Select ${key}`}
                  className="justify-center !gap-0"
                />
              </td>
            )}
            {hasLeading && (
              <td
                role="cell"
                className={`${cellPad} w-10 text-center`}
              >
                {renderRowLeading?.(item)}
              </td>
            )}
            {hasExpander && (
              <td role="cell" className={`${cellPad} w-8`} onClick={(e) => e.stopPropagation()}>
                {expandable && <button
                  type="button"
                  aria-label={expanded ? 'Collapse row' : 'Expand row'}
                  aria-expanded={expanded}
                  onClick={() => onToggleExpanded?.(key)}
                  className="flex items-center justify-center rounded p-0.5 text-text-tertiary hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/60"
                >
                  <motion.span
                    animate={{ rotate: expanded ? 90 : 0 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                    className="flex"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </motion.span>
                </button>}
              </td>
            )}
            {columns.map((column) => {
              const alignCls =
                column.align === 'center'
                  ? 'text-center'
                  : column.align === 'right'
                    ? 'text-right'
                    : 'text-left';
              return (
                <td
                  key={column.key}
                  data-column-key={column.key}
                  role="cell"
                  className={`${cellPad} text-sm text-text-primary/90 ${alignCls} ${column.width ?? ''}`}
                  style={columnWidths?.[column.key] != null
                    ? { width: `${columnWidths[column.key]}px`, overflow: 'hidden' }
                    : undefined}
                >
                  {column.render
                    ? column.render(item, index)
                    : String((item as Record<string, unknown>)[column.key] ?? '')}
                </td>
              );
            })}
            {renderRowActions && (
              <td
                role="cell"
                className={`${cellPad} text-right w-12`}
                onClick={(e) => e.stopPropagation()}
              >
                {renderRowActions(item)}
              </td>
            )}
          </motion.tr>
          {hasExpander && expandable && (
            <tr role="row" className={zebraCls}>
              <td role="cell" colSpan={colCount} className="p-0">
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      key="expanded"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 py-3">{renderExpanded?.(item)}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </td>
            </tr>
          )}
        </Fragment>
      );
    },
    [
      columns,
      cellPad,
      zebra,
      rowClassName,
      onRowClick,
      onRowContextMenu,
      isRowClickable,
      rowKey,
      renderRowLeading,
      renderRowActions,
      hasLeading,
      hasExpander,
      colCount,
      expandedKeys,
      onToggleExpanded,
      renderExpanded,
      isRowExpandable,
      columnWidths,
      selection,
      hasSelection,
      animateRows,
    ],
  );

  return (
    <tbody className="divide-y divide-white/[0.04]" aria-busy={loading || undefined}>
      {loading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <SkeletonRow
            key={i}
            columns={columns.length}
            hasActions={!!renderRowActions}
            hasLeading={hasLeading}
            hasExpander={hasExpander}
          />
        ))
      ) : data.length === 0 ? (
        <tr role="row">
          <td role="cell" colSpan={colCount} className="px-4 py-12 text-center">
            {emptyState ?? (
              <div className="flex flex-col items-center gap-2 text-text-tertiary">
                <p className="text-sm">No records</p>
              </div>
            )}
          </td>
        </tr>
      ) : animateRows ? (
        <AnimatePresence initial={false}>{data.map(renderRow)}</AnimatePresence>
      ) : (
        data.map(renderRow)
      )}
    </tbody>
  );
}

export const DataGridBody = memo(DataGridBodyInner) as <T>(
  props: DataGridBodyProps<T>,
) => ReactNode;
